import { Button, Card, Form, Heading, Select } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next'
import styles from './StartSimulationCard.module.scss'
import { FormProvider, useForm } from 'react-hook-form'
import { PostalCodeAndTown } from 'components/inputs'
import { ClientType, EnergyType, StartSimulationFormInputs, StartSimulationFormProps } from 'components/start-simulation-card/types'
import React, { useEffect, useState } from 'react'
import parse from 'html-react-parser'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { CustomerFlows, InputGroupKeys, PersonalDataFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { AddressFields } from 'components/inputs/address/types'
import { useHypertune } from 'generated/hypertune.react'
import { checkIsEavOrFixed, startSimulationOrRegistration } from 'utils/customerFlow'
import { IS_PROD } from 'constants/envs'
import { SimulationSteps } from 'features/simulation/types'
import { goToStep, updateAllInputs, updateInput, updateSimulatedPropositions } from 'store/customer/slice'
import { getUsableProducts } from 'utils/products'
import { determineSimulationUsage, requestAndSavePropositions } from 'features/simulation/utils'
import { CtaTrackingVariant } from 'types/simulation'
import { log } from 'utils/logging'
import Link from 'components/link/Link'
import { PhoneAvailability } from 'components/phone-availibility/PhoneAvailability'
import classNames from 'classnames'

const StartSimulationCard = ({ allowedClientTypes, specificProduct, fullWidth, onSubmitExtraActions }: StartSimulationFormProps) => {
  // i18n
  const { t } = useTranslation(['simulation', 'common', 'lookingForHelp'])

  // Hypertune
  const hypertune = useHypertune()
  const hypertuneCtaText = hypertune.callToActionButtonText({ fallback: t('common:calculateProposal') })

  // Redux store
  const { inputs, propositions } = useStoreSelector((store) => store.customer)
  const { personalData, simulation } = inputs
  const { didSimulation, currentStep } = useStoreSelector((store) => store.customer.flows.simulation)
  const dispatch = useStoreDispatch()

  // React Hook Form
  const hookForm = useForm<StartSimulationFormInputs>({
    defaultValues: {
      clientType: personalData[PersonalDataFieldKeys.IS_COMPANY] ? ClientType.PROFESSIONAL : ClientType.RESIDENTIAL,
      energyType: personalData[PersonalDataFieldKeys.NEEDS_GAS] ? EnergyType.ELECTRICITY_GAS : EnergyType.ELECTRICITY,
      postalCode: personalData[PersonalDataFieldKeys.DELIVERY_ADDRESS]?.[AddressFields.POSTAL_CODE] || ''
    },
    mode: 'onBlur'
  })
  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch
  } = hookForm
  const watchEnergyType = watch('energyType')

  // Local state
  const [loading, setLoading] = useState<boolean>(false)

  // Constants
  const isEavOrFixed = checkIsEavOrFixed(inputs.simulation.chosenSimulationType)

  /**
   * Triggered everytime the personalData inputs change (Needed because the page loads before the values are in the store)
   * Sets the values in the form if they change in the store
   */
  useEffect(() => {
    setValue('clientType', personalData[PersonalDataFieldKeys.IS_COMPANY] ? ClientType.PROFESSIONAL : ClientType.RESIDENTIAL)
    setValue('energyType', personalData[PersonalDataFieldKeys.NEEDS_GAS] ? EnergyType.ELECTRICITY_GAS : EnergyType.ELECTRICITY)
    setValue('postalCode', personalData[PersonalDataFieldKeys.DELIVERY_ADDRESS]?.[AddressFields.POSTAL_CODE] || '')
  }, [personalData, setValue])

  /**
   * Handles the form submit after validation by React Hook Form
   *
   * @param {StartSimulationFormInputs} data
   */
  const onSubmit = async (data: StartSimulationFormInputs) => {
    setLoading(true)

    try {
      // Redirect to the postal code step if no postal code was filled in
      if (!data.postalCode) {
        dispatch(goToStep({ flow: CustomerFlows.SIMULATION, step: SimulationSteps.POSTAL_CODE }))
      }

      // Define booleans
      const isCompany = data.clientType === ClientType.PROFESSIONAL
      const needsGas = data.energyType === EnergyType.ELECTRICITY_GAS

      // Check if the input has changed
      const hasChangedInput =
        personalData[PersonalDataFieldKeys.DELIVERY_ADDRESS][AddressFields.POSTAL_CODE] !== data.postalCode ||
        personalData[PersonalDataFieldKeys.IS_COMPANY] !== isCompany ||
        personalData[PersonalDataFieldKeys.NEEDS_GAS] !== needsGas

      // Update the store
      dispatch(
        updateAllInputs({
          inputs: {
            ...inputs,
            [InputGroupKeys.PERSONAL_DATA]: {
              ...personalData,
              [PersonalDataFieldKeys.IS_COMPANY]: isCompany,
              [PersonalDataFieldKeys.NEEDS_GAS]: needsGas,
              [PersonalDataFieldKeys.DELIVERY_ADDRESS]: {
                ...personalData[PersonalDataFieldKeys.DELIVERY_ADDRESS],
                postalCode: data.postalCode,
                townCode: data.townCode,
                townName: data.townName
              }
            },
            [InputGroupKeys.SIMULATION]: {
              ...simulation,
              [SimulationFieldKeys.REGION]: data.region
            }
          }
        })
      )

      // Extra actions after the form submission
      await onSubmitExtraActions?.()

      // If they changed the input & already did a simulation OR there was a specific product selected,
      // we need to reset the simulated propositions & simulate again for the new usable products
      if ((didSimulation && hasChangedInput) || propositions.simulateSpecificProduct) {
        // Reset the simulated propositions
        dispatch(updateSimulatedPropositions({ propositions: null }))
        // Get the new usable products
        const usableProducts = getUsableProducts({ region: data.region })

        // If they changed the input of the form, we need to update the usage
        if (hasChangedInput) {
          dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.USAGE, value: determineSimulationUsage() }))
        }

        await requestAndSavePropositions({ products: usableProducts })
      }

      const startFromUsage = data.postalCode && [SimulationSteps.POSTAL_CODE, SimulationSteps.ENERGY_TYPE].includes(currentStep)

      return startSimulationOrRegistration({
        ctaTrackingVariant: CtaTrackingVariant.START_SIMULATION_FORM,
        specificProduct,
        startFromUsage
      })
    } catch (error) {
      log({ error: 'Failed to submit StartSimulationForm', identifier: '[simulation:StartSimulationForm]' })
      return
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles['start-simulation-card-wrapper']}>
      <Card color="soft" className={classNames(styles['start-simulation-card'], { [styles.fullwidth]: fullWidth })}>
        <Heading as="h2" variant="h4" serif>
          {t('simulation:startSimulationCard.title')}
        </Heading>

        <FormProvider {...hookForm}>
          <Form onSubmit={handleSubmit(onSubmit)} className={styles['start-simulation-card-form']}>
            <PostalCodeAndTown
              hideErrorMessage
              hasRegionField
              hiddenLabel
              hideTownSelectField
              required={false}
              setFetchingTowns={setLoading}
            />

            <Select
              {...register('clientType', {
                required: t('validation:required')
              })}
              disabled={allowedClientTypes?.length === 1}
              error={!!errors?.clientType?.message}
              hiddenLabel
              label={t('simulation:fields.clientType.label')}
            >
              <option key={ClientType.RESIDENTIAL} value={ClientType.RESIDENTIAL}>
                {t('common:residential')}
              </option>
              <option key={ClientType.PROFESSIONAL} value={ClientType.PROFESSIONAL}>
                {t('common:company')}
              </option>
            </Select>

            <Select
              {...register('energyType', {
                required: t('validation:required')
              })}
              error={errors?.energyType?.message}
              hiddenLabel
              label={t('simulation:fields.energyType.label')}
              prefix={{
                as: watchEnergyType === EnergyType.ELECTRICITY ? 'electricity' : 'electricityGas'
              }}
            >
              <option key={EnergyType.ELECTRICITY} value={EnergyType.ELECTRICITY}>
                {t('common:electricity')}
              </option>
              <option key={EnergyType.ELECTRICITY_GAS} value={EnergyType.ELECTRICITY_GAS}>
                {t('common:electricityAndGas')}
              </option>
            </Select>

            <Button className={styles.cta} isFullwidthMobile loading={loading} size="large" type="submit">
              {didSimulation || isEavOrFixed ? t('common:switchNow') : parse(hypertuneCtaText)}
            </Button>
          </Form>
        </FormProvider>

        <small>
          <strong>{t('lookingForHelp:needHelp')}</strong>{' '}
          <Link as="a" href="tel:028993300" size="small">
            {t('lookingForHelp:callNumber')}
          </Link>
          . <PhoneAvailability />
        </small>
      </Card>
    </div>
  )
}

export default StartSimulationCard
